/** Shared placement maths for props: where a tile or corner is, how high its top stands, which way is "towards". */
import * as THREE from 'three';

import { KIND_HEIGHT, STEP, type PlanetView } from '../planet/view';

export const UP = new THREE.Vector3(0, 1, 0);
export const X = new THREE.Vector3(1, 0, 0);

/** Merges geometries with positions and normals into one indexed geometry. */
export function mergeGeometries(list: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const pos: number[] = [];
  const nor: number[] = [];
  const idx: number[] = [];
  let off = 0;
  for (const g of list) {
    const p = g.attributes['position'];
    const n = g.attributes['normal'];
    if (!p || !n) continue;
    const pa = p.array as Float32Array;
    const na = n.array as Float32Array;
    for (let i = 0; i < pa.length; i++) {
      pos.push(pa[i] ?? 0);
      nor.push(na[i] ?? 0);
    }
    const count = p.count;
    if (g.index) for (let i = 0; i < g.index.count; i++) idx.push((g.index.array[i] ?? 0) + off);
    else for (let i = 0; i < count; i++) idx.push(i + off);
    off += count;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  out.setIndex(idx);
  return out;
}

/** Spheres stacked on one axis: [radius, height] each. */
export const spheres = (list: [number, number][]): THREE.BufferGeometry =>
  mergeGeometries(list.map(([r, y]) => new THREE.SphereGeometry(r, 12, 8).translate(0, y, 0)));

/** A tile's top above the planet's radius: its level, plus how high its kind stands. */
export const tileTop = (view: PlanetView, i: number): number =>
  (view.elev[i] ?? 0) * STEP + (KIND_HEIGHT[view.kind[i] ?? 0] ?? 0);

export const centerOf = (view: PlanetView, i: number, out: THREE.Vector3): THREE.Vector3 =>
  out.set(
    view.grid.centers[i * 3] ?? 0,
    view.grid.centers[i * 3 + 1] ?? 0,
    view.grid.centers[i * 3 + 2] ?? 0,
  );

export const cornerDirOf = (view: PlanetView, c: number, out: THREE.Vector3): THREE.Vector3 =>
  out.set(
    view.grid.cornerDirs[c * 3] ?? 0,
    view.grid.cornerDirs[c * 3 + 1] ?? 0,
    view.grid.cornerDirs[c * 3 + 2] ?? 0,
  );

const _q = new THREE.Quaternion();
const _x = new THREE.Vector3();
const _b = new THREE.Vector3();
const _z = new THREE.Vector3();

/** The turn about `up` (a unit vector) that points a model's x axis at `target` (a unit vector). */
export function yawTowards(up: THREE.Vector3, target: THREE.Vector3): number {
  _q.setFromUnitVectors(UP, up);
  _b.copy(target).addScaledVector(up, -target.dot(up)).normalize();
  _x.copy(X).applyQuaternion(_q);
  return Math.atan2(_z.crossVectors(_x, _b).dot(up), _x.dot(_b));
}
